import { inject, Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { switchMap, tap } from 'rxjs/operators';
import { ClosCustomerInfo, ClosEntitlement, ClosProduct } from './types';
import { FetchProducts, ProductPurchase, FetchCustomerInfo } from './actions';
import { PurchasesApi } from './purchases.api';
import { SignOut } from '@apps/clos-sessions/state/auth';

export interface SubscriptionsStateModel {
  products: ClosProduct[];
  customerInfo: ClosCustomerInfo;
  /**
   * Flips true after the first successful `FetchCustomerInfo` (or purchase)
   * completes. Consumers that want to avoid flashing UI before the user's
   * entitlement status is known should gate on this flag, because the
   * default `customerInfo` is an empty shape that is indistinguishable
   * from a free user.
   */
  customerInfoLoaded: boolean;
}

const defaults: SubscriptionsStateModel = {
  products: [],
  customerInfo: {
    managementURLs: [],
    entitlements: [],
  },
  customerInfoLoaded: false,
};

@State<SubscriptionsStateModel>({
  name: 'subscriptions',
  defaults,
})
@Injectable()
export class SubscriptionsState {
  static customerInfo = createSelector(
    [SubscriptionsState],
    (state: SubscriptionsStateModel) => state.customerInfo,
  );

  static customerInfoLoaded = createSelector(
    [SubscriptionsState],
    (state: SubscriptionsStateModel) => state.customerInfoLoaded,
  );

  static activeEntitlements = createSelector(
    [SubscriptionsState.customerInfo],
    (customerInfo: ClosCustomerInfo) => {
      return customerInfo.entitlements.filter(({ isActive }) => isActive);
    },
  );

  static hasActiveEntitlement = createSelector(
    [SubscriptionsState.activeEntitlements],
    (entitlements: ClosEntitlement[]) => entitlements.length > 0,
  );

  static products = createSelector(
    [SubscriptionsState],
    (state: SubscriptionsStateModel) => state.products,
  );

  static productById = (productId: string) =>
    createSelector([SubscriptionsState.products], (products: ClosProduct[]) => {
      return products.find((product) => product.id === productId);
    });

  protected api = inject(PurchasesApi);

  /** Account-scoped data must not survive a logout (the next account can sign in without a reload). */
  @Action(SignOut)
  protected onSignOut({ setState }: StateContext<SubscriptionsStateModel>): void {
    setState(defaults);
  }

  @Action(FetchCustomerInfo)
  protected onFetchCustomerInfo(ctx: StateContext<SubscriptionsStateModel>) {
    return this.api
      .getCustomerInfo()
      .pipe(tap((customerInfo) => ctx.patchState({ customerInfo, customerInfoLoaded: true })));
  }

  @Action(FetchProducts, { cancelUncompleted: true })
  protected onFetchProducts(ctx: StateContext<SubscriptionsStateModel>) {
    return this.api.getProducts().pipe(tap((products) => ctx.patchState({ products })));
  }

  @Action(ProductPurchase, { cancelUncompleted: true })
  protected onProductPurchase(
    ctx: StateContext<SubscriptionsStateModel>,
    { payload }: ProductPurchase,
  ) {
    return this.api.purchase(payload).pipe(
      switchMap((customerInfo) => {
        return this.api
          .confirmPurchaseCompletion(payload)
          .pipe(tap(() => ctx.patchState({ customerInfo, customerInfoLoaded: true })));
      }),
    );
  }
}
